import { MAIN_PORTAL_INSTANCE_ID } from "./portal-instance"

/**
 * One rule for issue and project visibility across portals: a portal is either
 * checked or not, and nothing checked is hidden. Hiding only flips `isVisible`:
 * the support portal lists a customer's own requests by `portalInstanceIds`
 * whatever `isVisible` says, so the array has to survive a hide.
 */
export interface PortalScope {
	isVisible: boolean | null | undefined
	portalInstanceIds: readonly string[] | null | undefined
}

export interface PortalScopeWrite {
	isVisible: boolean
	portalInstanceIds: string[]
}

/** Portals the item shows on. The legacy empty array on a visible item is Main. */
export function shownPortalIds(scope: PortalScope): string[] {
	if (scope.isVisible !== true) return []
	const ids = scope.portalInstanceIds ?? []
	if (ids.length === 0) return [MAIN_PORTAL_INSTANCE_ID]
	return [...ids]
}

/** Every portal id in display order: Main first, then the named portals. */
export function orderedPortalIds(namedPortalIds: readonly string[]): string[] {
	return [MAIN_PORTAL_INSTANCE_ID, ...namedPortalIds]
}

/**
 * The write for a set of checked portals. Ids of deleted portals drop out. An
 * empty set hides the item and keeps `keepWhenHidden` as its stored portals.
 */
export function portalScopeFor(
	checked: Iterable<string>,
	namedPortalIds: readonly string[],
	keepWhenHidden: readonly string[] = [],
): PortalScopeWrite {
	const set = new Set(checked)
	const ids = orderedPortalIds(namedPortalIds).filter((id) => set.has(id))
	if (ids.length === 0)
		return { isVisible: false, portalInstanceIds: [...keepWhenHidden] }
	return { isVisible: true, portalInstanceIds: ids }
}

/**
 * Checks or unchecks one portal. Starts from where the item shows today, never
 * from the stored array: a hidden item can still hold the portals it was on,
 * and those must not come back with the next check.
 */
export function togglePortal(
	scope: PortalScope,
	portalId: string,
	value: boolean,
	namedPortalIds: readonly string[],
): PortalScopeWrite {
	const checked = new Set(shownPortalIds(scope))
	if (value) checked.add(portalId)
	else checked.delete(portalId)
	return portalScopeFor(checked, namedPortalIds, scope.portalInstanceIds ?? [])
}

/** Removes one portal, for "hide everything on this portal". */
export function removePortal(
	scope: PortalScope,
	portalId: string,
	namedPortalIds: readonly string[],
): PortalScopeWrite {
	return togglePortal(scope, portalId, false, namedPortalIds)
}

export function sameScope(scope: PortalScope, next: PortalScopeWrite): boolean {
	const ids = scope.portalInstanceIds ?? []
	return (
		(scope.isVisible ?? false) === next.isVisible &&
		ids.length === next.portalInstanceIds.length &&
		ids.every((id, i) => id === next.portalInstanceIds[i])
	)
}

export interface NamedPortal {
	id: string
	name: string
}

export function portalName(
	portalId: string,
	namedPortals: readonly NamedPortal[],
): string {
	if (portalId === MAIN_PORTAL_INSTANCE_ID) return "Main portal"
	return namedPortals.find((p) => p.id === portalId)?.name ?? "Portal"
}

/** Button label: where the item shows. */
export function summarizePortals(
	shown: readonly string[],
	namedPortals: readonly NamedPortal[],
): string {
	const known = new Set(orderedPortalIds(namedPortals.map((p) => p.id)))
	const ids = shown.filter((id) => known.has(id))
	if (ids.length === 0) return "Hidden"
	if (ids.length === known.size && known.size > 1) return "All portals"
	if (ids.length === 1) return portalName(ids[0]!, namedPortals)
	return `${ids.length} portals`
}

// A thread with no portal only counts as a Main portal request when it came in
// through the portal itself; an email or Slack thread was never on any portal.
const MAIN_PORTAL_ORIGINS = new Set(["PORTAL", "UPVOTE", "SUPPORT_PORTAL"])

export interface RequestSource {
	portalInstanceId?: string | null
	origin?: string | null
}

/** The portal a thread came in through, or null when it has none. */
export function requestPortalId(thread: RequestSource): string | null {
	if (thread.portalInstanceId) return thread.portalInstanceId
	if (thread.origin && MAIN_PORTAL_ORIGINS.has(thread.origin))
		return MAIN_PORTAL_INSTANCE_ID
	return null
}

/**
 * Portals customers asked for this item on but don't see it on yet, with the
 * number of requests each, most requested first.
 */
export function suggestedPortals(
	scope: PortalScope,
	threads: readonly RequestSource[],
	namedPortalIds: readonly string[],
): { portalId: string; count: number }[] {
	const known = new Set(orderedPortalIds(namedPortalIds))
	const shown = new Set(shownPortalIds(scope))
	const counts = new Map<string, number>()
	for (const thread of threads) {
		const portalId = requestPortalId(thread)
		if (!portalId || !known.has(portalId) || shown.has(portalId)) continue
		counts.set(portalId, (counts.get(portalId) ?? 0) + 1)
	}
	return [...counts]
		.map(([portalId, count]) => ({ portalId, count }))
		.sort((a, b) => b.count - a.count)
}

/** The items a portal doesn't show, for offering to show them there. */
export function notShownOn<T extends PortalScope>(
	items: readonly T[],
	portalId: string | null,
): T[] {
	if (!portalId) return []
	return items.filter((item) => !shownPortalIds(item).includes(portalId))
}

/**
 * Where a pending item goes when published: the portals its requests came in
 * through, stored on it at link time, or Main when it has none.
 */
export function pendingTargets(
	scope: PortalScope,
	namedPortalIds: readonly string[],
): string[] {
	const known = new Set(orderedPortalIds(namedPortalIds))
	const ids = (scope.portalInstanceIds ?? []).filter((id) => known.has(id))
	if (ids.length === 0) return [MAIN_PORTAL_INSTANCE_ID]
	return orderedPortalIds(namedPortalIds).filter((id) => ids.includes(id))
}
